import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
	TextField,
	Button,
	Dialog,
	DialogContent,
	DialogActions,
	InputAdornment,
	IconButton
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";

import { auth } from "../services/firebase";
import { captureError } from "../lib/errorReporting";

const Container = styled.div`
	height: 100vh;
	width: 100%;

	background: linear-gradient(
		0deg,
		rgba(0, 0, 0, 1) 0%,
		rgba(196, 45, 62, 1) 100%
	);

	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;

	img {
		width: 200px;
		height: auto;
		margin-bottom: 32px;
	}

	.login-container {
		position: relative;
		background: #f0f0f0;

		border-radius: 4px;
		box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.2);

		display: flex;
		flex-direction: column;
		align-items: center;

		padding: 32px;
	}

	form {
		display: flex;
		flex-direction: column;
		align-items: center;

		width: 100%;

		& > * {
			margin-bottom: 16px;
		}

		& > input {
			width: 100%;
		}

		.button {
			max-width: 400px;
			width: 100%;
		}

		.input {
			max-width: 300px;
			width: 200%;
		}
	}

	.helper-text {
		color: rgba(0, 0, 0, 0.6);
		font-size: 14px;
		margin-bottom: 8px;
		text-align: center;
	}
`;

export const ResetPassword = () => {
	const location = useLocation();
	const history = useHistory();
	const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const oobCode = query.get("oobCode") ?? "";
	const mode = query.get("mode") ?? "";

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [error, setError] = useState("");
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isVerified, setIsVerified] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");

	useEffect(() => {
		if (!oobCode) {
			setError("This reset link is missing a code.");
			setIsDialogOpen(true);
			return;
		}
		if (mode && mode !== "resetPassword") {
			setError("This link is not for a password reset.");
			setIsDialogOpen(true);
			return;
		}

		auth
			.verifyPasswordResetCode(oobCode)
			.then((verifiedEmail) => {
				setEmail(verifiedEmail ?? "");
				setIsVerified(true);
			})
			.catch((verifyErr) => {
				captureError(verifyErr, { view: "ResetPassword", action: "verifyPasswordResetCode" });
				setError("This reset link is invalid or has expired.");
				setIsDialogOpen(true);
			});
	}, [mode, oobCode]);

	const handleSubmit = async (event) => {
		if (event && typeof event.preventDefault === "function") {
			event.preventDefault();
		}
		if (!isVerified) {
			setError("This reset link is invalid or has expired.");
			setIsDialogOpen(true);
			return;
		}
		if (!password || password.length < 6) {
			setError("Password must be at least 6 characters.");
			setIsDialogOpen(true);
			return;
		}
		if (password !== confirmPassword) {
			setError("Passwords do not match.");
			setIsDialogOpen(true);
			return;
		}

		try {
			setIsSubmitting(true);
			await auth.confirmPasswordReset(oobCode, password);
			setSuccessMessage("Your password has been reset. You can now sign in.");
			setIsDialogOpen(true);
		} catch (err) {
			captureError(err, { view: "ResetPassword", action: "confirmPasswordReset" });
			setError(err?.message || "Unable to reset password.");
			setIsDialogOpen(true);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDialogClose = () => {
		setIsDialogOpen(false);
		if (successMessage) {
			history.push("/login");
		}
	};

	return (
		<Container>
			<div className="login-container">
				<img alt="Bar Hopper" src="/logo2.png" />
				<form noValidate onSubmit={handleSubmit} style={{ width: "300px" }}>
					{email ? (
						<div className="helper-text">
							Resetting password for {email}
						</div>
					) : null}
					<TextField
						className="input"
						label="New password"
						variant="outlined"
						name="password"
						type={showPassword ? "text" : "password"}
						autoComplete="new-password"
						value={password}
						onChange={(e) => setPassword(e.target.value ?? "")}
						disabled={!isVerified || isSubmitting}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										edge="end"
										aria-label="toggle password visibility"
										onClick={() => setShowPassword((prev) => !prev)}
										color="primary"
									>
										{showPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							)
						}}
					/>
					<TextField
						className="input"
						label="Confirm password"
						variant="outlined"
						name="confirmPassword"
						type={showConfirmPassword ? "text" : "password"}
						autoComplete="new-password"
						value={confirmPassword}
						onChange={(e) => setConfirmPassword(e.target.value ?? "")}
						disabled={!isVerified || isSubmitting}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										edge="end"
										aria-label="toggle confirm password visibility"
										onClick={() => setShowConfirmPassword((prev) => !prev)}
										color="primary"
									>
										{showConfirmPassword ? (
											<VisibilityOff />
										) : (
											<Visibility />
										)}
									</IconButton>
								</InputAdornment>
							)
						}}
					/>
					<Button
						className="button"
						color="primary"
						variant="contained"
						disabled={!isVerified || isSubmitting}
						onClick={handleSubmit}
					>
						Reset Password
					</Button>
				</form>
			</div>
			<Dialog open={isDialogOpen} onClose={handleDialogClose}>
				<DialogContent>{successMessage || error}</DialogContent>
				<DialogActions>
					<Button
						color="primary"
						variant="contained"
						onClick={handleDialogClose}
					>
						OK
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
};
